import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Alert,
  Form,
  Input,
  Button,
  Row,
  Col,
  Select,
  Modal,
  Space,
  Checkbox,
  Divider,
} from 'antd';
import { useUserContext } from '../../../contexts/UserContext';
import { useForm } from 'antd/es/form/Form';
import { trimValues } from '../../../utils/trimValues';
import { useNotificationContext } from '../../../contexts/NotificationContext';
import { Plus, Trash } from 'react-bootstrap-icons';
import ConfirmModelComponent from '../../../components/model/ConfirmationModelComponent';
import { productCategoryService } from '../../../api/services/productCategoryService';
import type { ProductCategory } from '../../../types/productCategory';
import { referenceDataSyncService } from '../../../services/referenceDataSyncService';
import ProductCategoryTable from '../../../components/productCategory/ProductCategoryTable';
import { ActiveFilterEnum } from '../../../enums/ActiveFilterEnum';
import { useTranslation } from 'react-i18next';
import { getPoductCategoryList } from './getData';
import { useLocalData } from '../../../hooks/useLocalData';
import PrimarySquareBtn from '../../../components/buttons/PrimarySquareBtn';
import FormModelComponent from '../../../components/model/FormModelComponent';
import { normalize } from '../../../utils/normalizeWords';
import DefaultButton from '../../../components/buttons/DefaultButton';
import PrimaryButton from '../../../components/buttons/PrimaryButton';
import {
  REGEX_ALL_ALLOWED_SAFE_FILTER,
  REGEX_ALPHA_NUMERIC_MORE_SAFE_FILTER,
} from '../../../constants/regex';
import { useIsOnlineContext } from '../../../contexts/IsOnlineContext';
import { PermissionEnum } from '../../../enums/PermissionEnum';
import { usePermissions } from '../../../hooks/usePermissions';

const initialCategory = {
  mainCategory: '',
};

const ProductCategoryView: React.FC = () => {
  const [addCategoryForm] = useForm();
  const { t } = useTranslation(['productCategory', 'common']);

  const [newCategory, setNewCategory] = useState(initialCategory);

  const [authStatus, setAuthStatus] = useState<{ isAuthenticated: boolean; token: string | null }>({
    isAuthenticated: false,
    token: null,
  });

  const [isAddCategoryModalOpen, setIsAddCategoryModalOpen] = useState<boolean>(false);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState<boolean>(false);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState<boolean>(false);
  const [isRestoreConfirmationOpen, setIsRestoreConfirmationOpen] = useState<boolean>(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);

  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const loadingRef = useRef<boolean>(false);
  const [categoryLoading, setCategoryLoading] = useState<boolean>(false);
  const [isConfirmClicked, setIsConfirmClicked] = useState<boolean>(false);

  const [selectedMainCategories, setSelectedMainCategories] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<ActiveFilterEnum>(ActiveFilterEnum.ACTIVE);

  const offlineFilters = useMemo(() => {
    const filters: Record<string, string[]> = {};
    if (selectedMainCategories.length > 0) filters.mainCategory = selectedMainCategories;
    return filters;
  }, [selectedMainCategories]);

  const {
    data: productCategories,
    total: offlineTotal,
    loading: isOfflineDataLoading,
    refresh: refreshOfflineCategories,
  } = useLocalData({
    entity: 'productCategories',
    autoSync: false,
    page: currentPage,
    limit: pageSize,
    sortBy: 'mainCategory',
    sortOrder: 'asc',
    filters: offlineFilters,
  });

  const [isLoadingFilterData, setIsLoadingFilterData] = useState<boolean>(false);
  const [isLoadingCategories, setIsLoadingCategories] = useState<boolean>(false);

  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [editingCategory, setEditingCategory] = useState<ProductCategory | null>(null);
  const [editForm] = useForm();
  const [categoryRefreshRequest, setCategoryRefreshRequest] = useState<{
    key: number;
    page: number;
    filters: { mainCategories?: string[]; categories?: string[] };
  } | null>(null);

  const [uniqueCategoryOptions, setUniqueCategoryOptions] = useState<any[]>([]);
  const [uniqueMainCategoryOptions, setUniqueMainCategoryOptions] = useState<any[]>([]);

  const { successNotification, errorNotification } = useNotificationContext();
  const { IsAuthenticated, getUserToken } = useUserContext();

  const [enableClearFiltersBtn, setEnableClearFiltersBtn] = useState<boolean>(false);

  const { isOnline } = useIsOnlineContext();
  const { hasPermission } = usePermissions();
  const canManageProductCategories = hasPermission(PermissionEnum.MANAGE_PRODUCT_CATEGORIES);

  const getAllCategoryList = useCallback(
    async (
      page: number = currentPage,
      filters?: any,
      customPageSize?: number,
      status: ActiveFilterEnum = statusFilter
    ) => {
      if (!isOnline) return;

      setIsLoadingCategories(true);
      try {
        const pageLimit = customPageSize !== undefined ? customPageSize : pageSize;

        const paginationParams = {
          page,
          limit: pageLimit,
        };

        const filterParams: any = {
          ...paginationParams,
          status,
        };

        if (
          filters?.mainCategories &&
          Array.isArray(filters?.mainCategories) &&
          filters?.mainCategories.length > 0
        ) {
          filterParams.mainCategories = filters?.mainCategories;
        }

        if (
          filters?.categories &&
          Array.isArray(filters?.categories) &&
          filters?.categories.length > 0
        ) {
          filterParams.categories = filters?.categories;
        }

        const response = await getPoductCategoryList(filterParams);

        if (response.statusText === 'SUCCESS') {
          const categories = response.data?.data || response.data || [];
          const total = response.data?.total || 0;
          setCategories(categories);
          setTotal(total);
          setCurrentPage(page);
        } else {
          console.error(t('productCategoryFetchError'), response);
        }
      } catch (error) {
        console.error('Failed to load categories from backend:', error);
        setCategories([]);
        setTotal(0);
        setCurrentPage(page);
      } finally {
        setIsLoadingCategories(false);
      }
    },
    [isOnline, pageSize, statusFilter, t]
  );

  /* useEffect(() => {
    if (categories.length === 0) {
      loadCategories();
    }
  }, [loadCategories, categories.length]);

  useEffect(() => {
    return () => {
      loadingRef.current = false;
    };
  }, []); */

  useEffect(() => {
    const token = getUserToken();
    const isAuth = IsAuthenticated();
    setAuthStatus({ isAuthenticated: isAuth, token });
  }, [IsAuthenticated, getUserToken]);

  const handlePageChange = (page: number, size?: number) => {
    const newPageSize = size && size !== pageSize ? size : pageSize;

    setCurrentPage(page);
    if (size && size !== pageSize) {
      setPageSize(size);
    }

    const filters = {
      mainCategories: selectedMainCategories,
    };

    if (isOnline) {
      getAllCategoryList(page, filters, newPageSize, statusFilter);
    }
  };

  const fetchFilterData = useCallback(async () => {
    setIsLoadingFilterData(true);
    try {
      const productCategories = await referenceDataSyncService.getProductCategories();
      getUniqueCategories(productCategories);
    } catch (error) {
      console.error('Error fetching filter data:', error);
    } finally {
      setIsLoadingFilterData(false);
    }
  }, [errorNotification]);

  const requestCategoryRefresh = useCallback(
    (
      page: number = currentPage,
      filters: { mainCategories?: string[]; categories?: string[] } = {
        mainCategories: selectedMainCategories,
      }
    ) => {
      setCategoryRefreshRequest((previousRequest) => ({
        key: (previousRequest?.key ?? 0) + 1,
        page,
        filters,
      }));
    },
    [currentPage, selectedMainCategories]
  );

  useEffect(() => {
    fetchFilterData();
  }, [fetchFilterData]);

  useEffect(() => {
    if (!categoryRefreshRequest) return;

    const refreshCategoryData = async () => {
      await getAllCategoryList(categoryRefreshRequest.page, categoryRefreshRequest.filters);
      await fetchFilterData();
    };

    refreshCategoryData();
  }, [categoryRefreshRequest, fetchFilterData, getAllCategoryList]);

  useEffect(() => {
    if (!isOnline) {
      refreshOfflineCategories();
    }
  }, [isOnline, refreshOfflineCategories]);

  useEffect(() => {
    if (!isOnline) {
      const activeCategories = productCategories.filter((category) => category?.isActive !== false);
      setCategories(activeCategories);
      setTotal(offlineTotal);
    }
  }, [isOnline, offlineTotal, productCategories]);

  useEffect(() => {
    const filters = {
      mainCategories: selectedMainCategories,
    };

    const hasFilters =
      selectedMainCategories.length > 0 ||
      statusFilter !== 'active';

    if (!isOnline) {
      setCurrentPage(1);
      setEnableClearFiltersBtn(hasFilters);
    } else if (hasFilters) {
      getAllCategoryList(1, filters, undefined, statusFilter);
      setEnableClearFiltersBtn(true);
    } else {
      getAllCategoryList(1, {}, undefined, statusFilter);
      setEnableClearFiltersBtn(false);
    }
  }, [selectedMainCategories, statusFilter, isOnline, getAllCategoryList]);

  const clearAllFilters = () => {
    setSelectedMainCategories([]);
    setStatusFilter(ActiveFilterEnum.ACTIVE);

    if (isOnline) {
      getAllCategoryList(1, {}, undefined, ActiveFilterEnum.ACTIVE);
    }
  };

  const getUniqueCategories = (productCategories: ProductCategory[]) => {
    const uniqueMainCategories = Array.from(
      new Map(
        productCategories.map((category: { mainCategory: any }) => [
          category.mainCategory,
          category,
        ])
      ).values()
    ).sort((a: any, b: any) => a.mainCategory.localeCompare(b.mainCategory));
    setUniqueMainCategoryOptions(uniqueMainCategories);

    const uniqueCategories = Array.from(
      new Map(
        productCategories
          .filter((categories) => categories?.category !== null)
          .map((categoryItem: { category: any }) => [categoryItem.category, categoryItem])
      ).values()
    ).sort((a: any, b: any) => a.category.localeCompare(b.category));
    setUniqueCategoryOptions(uniqueCategories);
  };

  const handleNewCategoryButton = () => {
    if (!canManageProductCategories) return;

    setNewCategory(initialCategory);
    addCategoryForm.resetFields();
    setIsEdit(false);
    setIsAddCategoryModalOpen(true);
  };

  const handleCancel = () => {
    setNewCategory(initialCategory);
    addCategoryForm.resetFields();
    setIsAddCategoryModalOpen(false);
  };

  const getConfirmation = () => {
    addCategoryForm
      .validateFields()
      .then(() => {
        setIsConfirmationOpen(true);
      })
      .catch(() => { });
  };

  const getUpdateConfirmation = () => {
    editForm
      .validateFields()
      .then(() => {
        setIsConfirmationOpen(true);
      })
      .catch(() => { });
  };

  const handleCreate = async () => {
    const formValues = addCategoryForm.getFieldsValue();
    const trimmedValues = trimValues(formValues);

    // To disable the confirm button to avoid multiple clicks
    setIsConfirmClicked(true);

    await addCategoryForm.validateFields();

    // Close the popups to avoid multiple clicks
    setIsAddCategoryModalOpen(false);
    setIsConfirmationOpen(false);
    setCategoryLoading(true);

    const categoryArr = addCategoryForm.getFieldValue('category');

    const validSubCategories =
      categoryArr &&
      categoryArr
        .filter((sub: any) => sub?.value && sub?.value.trim() !== '')
        .map((sub: any) => ({ name: sub?.value.trim() }));

    const createData = {
      mainCategory: trimmedValues.mainCategory.trim(),
      subCategories: validSubCategories,
      customFields: formValues.customFields,
    };

    try {
      const createdCategories = await productCategoryService.createProductCategory(createData);

      if (createdCategories && createdCategories.length > 0) {
        await referenceDataSyncService.forceSyncEntity('productCategories');
      }

      successNotification(t('categoryCreateSuccess'));
      // handleCancel();
      setNewCategory(initialCategory);
      addCategoryForm.resetFields();

      requestCategoryRefresh(1, {});
    } catch (error: any) {
      console.error('Failed to create product category:', error);
      errorNotification(error?.message || t('categoryCreateFail'));
    } finally {
      setCategoryLoading(false);
      setIsConfirmClicked(false);
    }
  };

  const handleEdit = async (id: string) => {
    if (!canManageProductCategories) return;

    try {
      const categoryId = parseInt(id);
      const category = await productCategoryService.getProductCategoryById(categoryId);
      setEditingCategory(category);
      setIsEdit(true);
      setIsEditModalOpen(true);

      editForm.resetFields();
      editForm.setFieldsValue({
        mainCategory: category.mainCategory,
        category: category.category,
        customFields: category.customFields?.fields,
      });
    } catch (error) {
      console.error('Failed to load category for editing:', error);
      errorNotification('Failed to load category details');
    }
  };

  const handleUpdate = async () => {
    if (!editingCategory) return;

    // To disable the confirm button to avoid multiple clicks
    setIsConfirmClicked(true);

    const values = await editForm.validateFields();

    // Close the popups to avoid multiple clicks
    setIsEditModalOpen(false);
    setIsConfirmationOpen(false);
    setCategoryLoading(true);

    const trimmedValues = trimValues({
      mainCategory: values.mainCategory,
      category: values.category,
    });
    try {
      await productCategoryService.updateProductCategory(editingCategory.id, trimmedValues);

      // setIsEditModalOpen(false);
      setEditingCategory(null);
      editForm.resetFields();
      successNotification(t('categoryUpdateSuccess'));
      requestCategoryRefresh(1, {});
    } catch (error: any) {
      console.error('Failed to update category:', error);
      errorNotification(error?.message || t('categoryUpdateFail'));
    } finally {
      setCategoryLoading(false);
      setIsEdit(false);
      setIsConfirmClicked(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditModalOpen(false);
    setEditingCategory(null);
    editForm.resetFields();
    setIsEdit(false);
  };

  const getDeleteConfirmation = (id: string) => {
    const categoryId = parseInt(id);
    const selectedCategory = categories.find((cat) => cat.id === categoryId);

    if (selectedCategory && selectedCategory.productCount && selectedCategory.productCount > 0) {
      errorNotification(
        t('categoryDeleteWithProductsError', { count: selectedCategory.productCount })
      );
      return;
    }

    setIsDeleteConfirmationOpen(true);
    setSelectedCategoryId(categoryId);
  };

  const handleDelete = async () => {
    if (!canManageProductCategories) return;

    if (!selectedCategoryId) return;

    // To disable the confirm button and close the confirmation popup to avoid multiple clicks
    setIsConfirmClicked(true);
    setIsDeleteConfirmationOpen(false);
    setCategoryLoading(true);

    try {
      await productCategoryService.deleteProductCategory(selectedCategoryId);

      setSelectedCategoryId(null);
      successNotification(t('categoryDeleteSuccess'));
      requestCategoryRefresh(currentPage, {
        mainCategories: selectedMainCategories,
      });
    } catch (error: unknown) {
      console.error('Failed to delete category:', error);
      setIsDeleteConfirmationOpen(false);
      setSelectedCategoryId(null);

      if (
        error &&
        typeof error === 'object' &&
        'message' in error &&
        typeof error.message === 'string' &&
        error.message.includes('Cannot delete category')
      ) {
        errorNotification(error.message);
      } else {
        errorNotification(t('categoryDeleteFail'));
      }
    } finally {
      setIsConfirmClicked(false);
      setCategoryLoading(false);
    }
  };

  const cancelDeleteConfirmation = () => {
    setIsDeleteConfirmationOpen(false);
    setSelectedCategoryId(null);
  };

  const getRestoreConfirmation = (id: string) => {
    setSelectedCategoryId(parseInt(id));
    setIsRestoreConfirmationOpen(true);
  };

  const handleRestore = async () => {
    if (!selectedCategoryId) return;

    try {
      setIsConfirmClicked(true);
      setIsRestoreConfirmationOpen(false);
      setCategoryLoading(true);

      await productCategoryService.restoreProductCategory(selectedCategoryId);
      successNotification(t('categoryRestoreSuccess'));
      setSelectedCategoryId(null);
      requestCategoryRefresh(currentPage, {
        mainCategories: selectedMainCategories,
      });
    } catch (error: any) {
      errorNotification(error?.message || t('categoryRestoreFail'));
    } finally {
      setIsConfirmClicked(false);
      setCategoryLoading(false);
    }
  };

  const cancelConfirmation = () => {
    setIsConfirmationOpen(false);
  };

  if (loading) {
    return (
      <div className="accreditation-container">
        <div className="accreditation-counts">
          <div className="total-accreditation-container">
            <div className="total-accreditation total-back-bg">
              <div className="total-accreditation-text">
                <span>{t('loadingCategories')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <ConfirmModelComponent
        isOpen={isDeleteConfirmationOpen}
        onClickedCancel={cancelDeleteConfirmation}
        header={t('confirmDeleteTitle')}
        content={<p>{t('confirmDeleteMessage')}</p>}
        footerAction={{
          confimation: {
            label: t('confirm'),
            onClick: handleDelete,
            isDisabled: isConfirmClicked,
          },
          cancel: {
            label: t('cancel'),
            onClick: cancelDeleteConfirmation,
          },
        }}
        styleClasses="!w-1/4 min-w-[360px] md:min-w-[380px]"
      />

      <ConfirmModelComponent
        isOpen={isRestoreConfirmationOpen}
        onClickedCancel={() => setIsRestoreConfirmationOpen(false)}
        header={t('confirmRestoreTitle')}
        content={<p>{t('confirmRestoreMessage')}</p>}
        footerAction={{
          confimation: {
            label: t('confirm'),
            onClick: handleRestore,
            isDisabled: isConfirmClicked,
          },
          cancel: {
            label: t('cancel'),
            onClick: () => setIsRestoreConfirmationOpen(false),
          },
        }}
        styleClasses="!w-1/4 min-w-[360px] md:min-w-[380px]"
      />

      <FormModelComponent
        isOpen={isAddCategoryModalOpen}
        header={t('createCategoryTitle')}
        subheader={t('addCategoryDescription')}
        content={
          <div>
            <Form
              layout="vertical"
              form={addCategoryForm}
              name="addCategoryForm"
              // onFinish={getConfirmation}
              className="mb-4 w-full space-y-4 text-left"
            >
              <Form.Item
                name="mainCategory"
                label={t('mainCategory')}
                validateFirst
                rules={[
                  { required: true, message: t('mainCategoryRequired') },
                  {
                    validator: (_, value) => {
                      if (value && value.trim() === '') {
                        return Promise.reject(t('mainCategoryNameSpaceValidation'));
                      }
                      return Promise.resolve();
                    },
                  },
                  {
                    validator: (_: any, value: any) => {
                      if (!value) return Promise.resolve();
                      // Allowing letters, numbers space comma dash forward slash fullstop and paranthesis
                      // const regex = /^[A-Za-zÁÉÍÓÚáéíóúÑñÜü0-9 ,\-\/\.\(\)]*$/;
                      const regex = REGEX_ALPHA_NUMERIC_MORE_SAFE_FILTER;
                      return regex.test(String(value))
                        ? Promise.resolve()
                        : Promise.reject(t('invalidInput'));
                    },
                  },
                  {
                    validator: (_, value) => {
                      if (!value || value.trim() === '') {
                        return Promise.resolve();
                      }

                      const trimmedLength = value.replace(/\s/g, '').length;
                      if (trimmedLength < 3) {
                        return Promise.reject(t('mainCategoryNameMinLengthValidaiton'));
                      }
                      return Promise.resolve();
                    },
                  },
                  {
                    validator: (_, value) => {
                      if (!value || value.trim() === '') {
                        return Promise.resolve();
                      }

                      const trimmedLength = value.replace(/\s/g, '').length;
                      if (trimmedLength > 100) {
                        return Promise.reject(t('mainCategoryNameMaxLengthValidaiton'));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input
                  placeholder={t('mainCategoryPlaceholder')}
                  value={newCategory.mainCategory}
                  onChange={(e) => setNewCategory({ ...newCategory, mainCategory: e.target.value })}
                />
              </Form.Item>

              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">{t('customFields')}</label>
              </div>
              <Form form={addCategoryForm} layout="vertical">
                <Form.List name="customFields">
                  {(fields, { add, remove }) => (
                    <div>
                      {fields.map(({ key, name, ...rest }, index) => (
                        <React.Fragment key={key}>
                          <Row gutter={[8, 16]} align="top" className="mb-4">
                            <Col xs={24} md={6} lg={6}>
                              <Form.Item
                                {...rest}
                                name={[name, 'fieldName']}
                                validateFirst
                                rules={[
                                  { required: true, message: t('validation.fieldInputRequired') },
                                  {
                                    validator: (_, value) => {
                                      if (value && value.trim() === '') {
                                        return Promise.reject(
                                          t('validation.fieldInputSpaceValidation')
                                        );
                                      }
                                      return Promise.resolve();
                                    },
                                  },
                                  {
                                    validator: (_: any, value: any) => {
                                      if (!value) return Promise.resolve();
                                      // Allow letters, numbers, space, punctuation and special characters
                                      const regex = REGEX_ALL_ALLOWED_SAFE_FILTER;
                                      return regex.test(String(value))
                                        ? Promise.resolve()
                                        : Promise.reject(t('invalidInput'));
                                    },
                                  },
                                  {
                                    validator: (_, value) => {
                                      if (!value || value.trim() === '') {
                                        return Promise.resolve();
                                      }

                                      const trimmedLength = value.replace(/\s/g, '').length;
                                      if (trimmedLength < 1) {
                                        return Promise.reject(
                                          t('validation.fieldInputMinValidation')
                                        );
                                      }
                                      return Promise.resolve();
                                    },
                                  },
                                  {
                                    validator: (_, value) => {
                                      if (!value || value.trim() === '') {
                                        return Promise.resolve();
                                      }

                                      const trimmedLength = value.replace(/\s/g, '').length;
                                      if (trimmedLength > 100) {
                                        return Promise.reject(
                                          t('validation.fieldInputMaxValidation')
                                        );
                                      }
                                      return Promise.resolve();
                                    },
                                  },
                                ]}
                                className="!mb-0"
                              >
                                <Input placeholder={t('fieldName')} title={t('fieldName')} />
                              </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={6}>
                              <Form.Item
                                {...rest}
                                name={[name, 'placeholder']}
                                validateFirst
                                rules={[
                                  {
                                    required: true,
                                    message: t('validation.fieldPlaceholderRequired'),
                                  },
                                  {
                                    validator: (_, value) => {
                                      if (value && value.trim() === '') {
                                        return Promise.reject(
                                          t('validation.fieldPlaceholderSpaceValidation')
                                        );
                                      }
                                      return Promise.resolve();
                                    },
                                  },
                                  {
                                    validator: (_: any, value: any) => {
                                      if (!value) return Promise.resolve();
                                      // Allow letters, numbers, space, punctuation and special characters
                                      const regex = REGEX_ALL_ALLOWED_SAFE_FILTER;
                                      return regex.test(String(value))
                                        ? Promise.resolve()
                                        : Promise.reject(t('invalidInput'));
                                    },
                                  },
                                  {
                                    validator: (_, value) => {
                                      if (!value || value.trim() === '') {
                                        return Promise.resolve();
                                      }

                                      const trimmedLength = value.replace(/\s/g, '').length;
                                      if (trimmedLength < 1) {
                                        return Promise.reject(
                                          t('validation.fieldPlaceholdertMinValidation')
                                        );
                                      }
                                      return Promise.resolve();
                                    },
                                  },
                                  {
                                    validator: (_, value) => {
                                      if (!value || value.trim() === '') {
                                        return Promise.resolve();
                                      }

                                      const trimmedLength = value.replace(/\s/g, '').length;
                                      if (trimmedLength > 100) {
                                        return Promise.reject(
                                          t('validation.fieldPlaceholderMaxValidation')
                                        );
                                      }
                                      return Promise.resolve();
                                    },
                                  },
                                ]}
                                className="!mb-0"
                              >
                                <Input placeholder={t('placeholder')} title={t('placeholder')} />
                              </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={6}>
                              <Form.Item
                                {...rest}
                                name={[name, 'dataType']}
                                rules={[
                                  {
                                    required: true,
                                    message: t('validation.fieldDatatypeRequired'),
                                  },
                                ]}
                                className="!mb-0"
                              >
                                <Select placeholder={t('dataType')} title={t('dataType')}>
                                  <Select.Option value="string">
                                    {t('customDataTypes.string')}
                                  </Select.Option>
                                  <Select.Option value="number">
                                    {t('customDataTypes.integer')}
                                  </Select.Option>
                                  <Select.Option value="float">
                                    {t('customDataTypes.float')}
                                  </Select.Option>
                                  <Select.Option value="text">
                                    {t('customDataTypes.text')}
                                  </Select.Option>
                                  <Select.Option value="date">
                                    {t('customDataTypes.date')}
                                  </Select.Option>
                                </Select>
                              </Form.Item>
                            </Col>
                            <Col xs={18} md={9} lg={5}>
                              <Form.Item
                                {...rest}
                                name={[name, 'required']}
                                valuePropName="checked"
                                initialValue={false}
                                className="!mb-0"
                              >
                                <Checkbox>{t('required')}</Checkbox>
                              </Form.Item>
                            </Col>
                            <Col
                              xs={6}
                              md={3}
                              lg={1}
                              className="flex items-start justify-end text-right"
                            >
                              <Trash
                                onClick={() => remove(name)}
                                className="mt-3 text-red-500"
                                size={15}
                              />
                            </Col>
                          </Row>
                          {index < fields.length - 1 && (
                            <Divider className="my-4 bg-gray-300 md:hidden" />
                          )}
                        </React.Fragment>
                      ))}
                      {fields.length === 0 && (
                        <div className="md:text-md mb-4 py-4 text-center text-sm text-gray-500">
                          {t('noCustomFieldsAdded')}
                        </div>
                      )}
                      <Form.Item>
                        <Button
                          type="dashed"
                          icon={<Plus className="mt-1 h-4 w-4" />}
                          onClick={() => add()}
                          className="md:text-md block flex items-center gap-2 text-sm"
                        >
                          {t('addCustomField')}
                        </Button>
                      </Form.Item>{' '}
                    </div>
                  )}
                </Form.List>
              </Form>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700">{t('subCategories')}</label>
                </div>

                <Form.List name="category">
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map(({ key, name }, index) => (
                        <div key={key} className="mb-8 flex w-full">
                          <Form.Item
                            {...{ name: [name, 'value'] }}
                            validateFirst
                            rules={[
                              {
                                validator: (_, value) => {
                                  if (value && value.trim() === '') {
                                    return Promise.reject(
                                      new Error(t('subCategoryNameSpaceValidation'))
                                    );
                                  }
                                  return Promise.resolve();
                                },
                              },
                              {
                                validator: (_: any, value: any) => {
                                  if (!value) return Promise.resolve();
                                  // Allowing letters, numbers space comma dash forward slash fullstop and paranthesis
                                  // const regex = /^[A-Za-zÁÉÍÓÚáéíóúÑñÜü0-9 ,\-\/\.\(\)]*$/;
                                  const regex = REGEX_ALPHA_NUMERIC_MORE_SAFE_FILTER;
                                  return regex.test(String(value))
                                    ? Promise.resolve()
                                    : Promise.reject(t('invalidInput'));
                                },
                              },
                              {
                                validator: (_, value) => {
                                  if (!value || value.trim() === '') {
                                    return Promise.resolve();
                                  }

                                  const trimmedLength = value.replace(/\s/g, '').length;
                                  if (trimmedLength < 3) {
                                    return Promise.reject(t('categoryMinLength'));
                                  }
                                  return Promise.resolve();
                                },
                              },
                              {
                                validator: (_, value) => {
                                  if (!value || value.trim() === '') {
                                    return Promise.resolve();
                                  }

                                  const trimmedLength = value.replace(/\s/g, '').length;
                                  if (trimmedLength > 100) {
                                    return Promise.reject(t('categoryMaxLength'));
                                  }
                                  return Promise.resolve();
                                },
                              },
                            ]}
                            className="w-full"
                          >
                            <Input
                              placeholder={t('subCategoryPlaceholder', { indexVal: index + 1 })}
                              className="w-full"
                            />
                          </Form.Item>
                          {fields.length > 0 && (
                            <Trash
                              onClick={() => remove(name)}
                              className="ml-4 mt-3 text-red-500"
                            />
                          )}
                        </div>
                      ))}
                      {fields.length === 0 && (
                        <div className="md:text-md py-4 text-center text-sm text-gray-500">
                          {t('noSubCategoriesAdded')}
                        </div>
                      )}
                      <Form.Item>
                        <Button
                          type="dashed"
                          icon={<Plus className="mt-1 h-4 w-4" />}
                          onClick={() => add()}
                          className="md:text-md flex items-center gap-2 text-sm"
                        >
                          {t('addSubCategory')}
                        </Button>
                      </Form.Item>
                    </>
                  )}
                </Form.List>
              </div>
            </Form>
          </div>
        }
        onClickedCancel={handleCancel}
        footerAction={{
          confimation: {
            label: t('createCategory'),
            onClick: getConfirmation,
          },
          cancel: {
            label: t('cancel'),
            onClick: handleCancel,
          },
        }}
        // styleClasses="!w-1/2 min-w-[500px] md:min-w-[600px] z-10"
        contentStyles="mb-4 px-2 max-h-[65vh] md:max-h-[70vh] overflow-y-auto space-y-2"
        styleClasses="md:!w-1/2 lg:!w-2/5"
      />

      <div className="mt-4 flex w-full flex-col justify-start gap-4 md:flex-row md:items-center md:justify-between">
        <div className="items-start">
          <h2 className="text-xl font-medium text-headings-font-color">{t('productCategories')}</h2>
          <p className="text-base font-medium text-font-color">{t('manageProductCategories')}</p>
        </div>

        {canManageProductCategories && (
          <div className="flex justify-end">
            <PrimarySquareBtn handleOnClick={handleNewCategoryButton} disabled={!isOnline}>
              <div className="flex">
                <Plus className="h-6 w-6 text-white" />
                <p className="pt-1">{t('newCategory')}</p>
              </div>
            </PrimarySquareBtn>
          </div>
        )}
      </div>

      {/* filter section */}
      <div className="my-6">
        <section className="mb-8 w-full">
          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-800">{t('filters')}</h3>
              <Button
                type="link"
                onClick={clearAllFilters}
                className="hover:text-primary-dark text-sm text-primary"
                disabled={!enableClearFiltersBtn}
              >
                {t('clearFilters')}
              </Button>
            </div>

            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={8} lg={5}>
                <div className="mb-2">
                  <label className="text-sm font-medium text-gray-700">{t('mainCategory')}</label>
                </div>
                <Select
                  className="w-full [&>.ant-select-selector]:!rounded-lg"
                  placeholder={t('mainCategoryFieldPlaceholder')}
                  mode="multiple"
                  allowClear
                  onClear={() => setSelectedMainCategories([])}
                  maxTagCount={1}
                  maxTagTextLength={window.matchMedia('(min-width: 1600px)').matches ? 15 : 10}
                  value={selectedMainCategories}
                  onChange={setSelectedMainCategories}
                  loading={isLoadingFilterData}
                  /*  options={Array.from(new Set(categories)).map((category: any) => ({
                    label: category.mainCategory,
                    value: category.mainCategory,
                  }))} */
                  /* options={Array.from(new Map(categories.map((category: { mainCategory: any; }) => [category.mainCategory, category])).values()).map((category: any) => ({
                    label: category.mainCategory,
                    value: category.mainCategory,
                  }))} */
                  options={uniqueMainCategoryOptions.map((category: any) => ({
                    label: category.mainCategory,
                    value: category.mainCategory,
                  }))}
                  showSearch
                  filterOption={(input, option) =>
                    normalize(option?.label ?? '')
                      .toString()
                      .toLowerCase()
                      .includes(normalize(input.toLowerCase()))
                  }
                />
              </Col>

              <Col xs={24} sm={12} md={8} lg={5}>
                <div className="mb-2">
                  <label className="text-sm font-medium text-gray-700">{t('status')}</label>
                </div>
                <Select
                  className="w-full [&>.ant-select-selector]:!rounded-lg"
                  value={statusFilter}
                  disabled={!isOnline}
                  onChange={(value: ActiveFilterEnum) => {
                    setStatusFilter(value);
                    setCurrentPage(1);
                  }}
                  options={Object.values(ActiveFilterEnum).map((status) => ({
                    label: t(`common:statusFilter.${status}`),
                    value: status,
                  }))}
                />
              </Col>
            </Row>
          </div>
        </section>

        <div className="my-6">
          <ProductCategoryTable
            categories={categories}
            loading={categoryLoading || (isOnline ? isLoadingCategories : isOfflineDataLoading)}
            totalCategories={total}
            currentPage={currentPage}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            onEdit={handleEdit}
            onDelete={getDeleteConfirmation}
            onRestore={getRestoreConfirmation}
          />
        </div>
      </div>

      <div className="accreditation-container">
        <div className="accreditation-counts">
          {!authStatus.isAuthenticated && (
            <Alert
              message="Authentication Required"
              description="You need to be logged in to sync data with the server. Please log in to enable sync functionality."
              type="warning"
              showIcon
              style={{ marginBottom: '20px' }}
            />
          )}
        </div>
      </div>

      <Modal
        title={
          <div className="text-primary-font-gray mb-2 px-2 text-start text-xl font-bold">
            {t('editCategory')}
          </div>
        }
        open={isEditModalOpen}
        onCancel={handleCancelEdit}
        // onOk={handleUpdate}
        // okText={t('update')}
        // cancelText={t('cancel')}
        footer={null}
        width={500}
        centered
        className="md:!w-1/2 lg:!w-2/5"
      >
        <>
          <div className="mb-4 max-h-[65vh] space-y-2 overflow-y-auto px-2 md:max-h-[70vh]">
            <Form form={editForm} layout="vertical" className="mt-4">
              <Form.Item
                name="mainCategory"
                label={t('mainCategory')}
                validateFirst
                rules={[
                  { required: true, message: t('mainCategoryRequired') },
                  {
                    validator: (_, value) => {
                      if (value && value.trim() === '') {
                        return Promise.reject(t('mainCategoryNameSpaceValidation'));
                      }
                      return Promise.resolve();
                    },
                  },
                  {
                    validator: (_: any, value: any) => {
                      if (!value) return Promise.resolve();
                      // Allowing letters, numbers space comma dash forward slash fullstop and paranthesis
                      // const regex = /^[A-Za-zÁÉÍÓÚáéíóúÑñÜü0-9 ,\-\/\.\(\)]*$/;
                      const regex = REGEX_ALPHA_NUMERIC_MORE_SAFE_FILTER;
                      return regex.test(String(value))
                        ? Promise.resolve()
                        : Promise.reject(t('invalidInput'));
                    },
                  },
                  {
                    validator: (_, value) => {
                      if (!value || value.trim() === '') {
                        return Promise.resolve();
                      }

                      const trimmedLength = value.replace(/\s/g, '').length;
                      if (trimmedLength < 3) {
                        return Promise.reject(t('mainCategoryNameMinLengthValidaiton'));
                      }
                      return Promise.resolve();
                    },
                  },
                  {
                    validator: (_, value) => {
                      if (!value || value.trim() === '') {
                        return Promise.resolve();
                      }

                      const trimmedLength = value.replace(/\s/g, '').length;
                      if (trimmedLength > 100) {
                        return Promise.reject(t('mainCategoryNameMaxLengthValidaiton'));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input placeholder={t('mainCategoryPlaceholder')} />
              </Form.Item>

              {Boolean(editingCategory?.customFields?.fields?.length) && (
                <>
                  <div className="mb-4 flex items-center justify-between">
                    <label className="text-sm font-medium text-gray-700">{t('customFields')}</label>
                  </div>
                  <Form.List name="customFields">
                    {(fields, { add, remove }) => (
                      <fieldset disabled className="pointer-events-none opacity-70">
                        <div>
                          {fields.map(({ key, name, ...rest }, index) => {
                            const fieldData = editForm.getFieldValue(['customFields', name]);
                            const isExisting = fieldData?.key !== undefined;
                            return (
                              <React.Fragment key={key}>
                                <Row gutter={[8, 16]} align="top" className="mb-4">
                                  <Col xs={24} md={6} lg={6}>
                                    <Form.Item
                                      {...rest}
                                      name={[name, 'fieldName']}
                                      validateFirst
                                      rules={[
                                        {
                                          required: true,
                                          message: t('validation.fieldInputRequired'),
                                        },
                                        {
                                          validator: (_, value) => {
                                            if (value && value.trim() === '') {
                                              return Promise.reject(
                                                t('validation.fieldInputSpaceValidation')
                                              );
                                            }
                                            return Promise.resolve();
                                          },
                                        },
                                        {
                                          validator: (_: any, value: any) => {
                                            if (!value) return Promise.resolve();
                                            // Allow letters, numbers, space, punctuation and special characters
                                            const regex = REGEX_ALL_ALLOWED_SAFE_FILTER;
                                            return regex.test(String(value))
                                              ? Promise.resolve()
                                              : Promise.reject(t('invalidInput'));
                                          },
                                        },
                                        {
                                          validator: (_, value) => {
                                            if (!value || value.trim() === '') {
                                              return Promise.resolve();
                                            }

                                            const trimmedLength = value.replace(/\s/g, '').length;
                                            if (trimmedLength < 1) {
                                              return Promise.reject(
                                                t('validation.fieldInputMinValidation')
                                              );
                                            }
                                            return Promise.resolve();
                                          },
                                        },
                                        {
                                          validator: (_, value) => {
                                            if (!value || value.trim() === '') {
                                              return Promise.resolve();
                                            }

                                            const trimmedLength = value.replace(/\s/g, '').length;
                                            if (trimmedLength > 100) {
                                              return Promise.reject(
                                                t('validation.fieldInputMaxValidation')
                                              );
                                            }
                                            return Promise.resolve();
                                          },
                                        },
                                      ]}
                                      className="!mb-0"
                                    >
                                      <Input placeholder={t('fieldName')} title={t('fieldName')} />
                                    </Form.Item>
                                  </Col>
                                  <Col xs={24} md={12} lg={6}>
                                    <Form.Item
                                      {...rest}
                                      name={[name, 'placeholder']}
                                      validateFirst
                                      rules={[
                                        {
                                          required: true,
                                          message: t('validation.fieldPlaceholderRequired'),
                                        },
                                        {
                                          validator: (_, value) => {
                                            if (value && value.trim() === '') {
                                              return Promise.reject(
                                                t('validation.fieldPlaceholderSpaceValidation')
                                              );
                                            }
                                            return Promise.resolve();
                                          },
                                        },
                                        {
                                          validator: (_: any, value: any) => {
                                            if (!value) return Promise.resolve();
                                            // Allow letters, numbers, space, punctuation and special characters
                                            const regex = REGEX_ALL_ALLOWED_SAFE_FILTER;
                                            return regex.test(String(value))
                                              ? Promise.resolve()
                                              : Promise.reject(t('invalidInput'));
                                          },
                                        },
                                        {
                                          validator: (_, value) => {
                                            if (!value || value.trim() === '') {
                                              return Promise.resolve();
                                            }

                                            const trimmedLength = value.replace(/\s/g, '').length;
                                            if (trimmedLength < 1) {
                                              return Promise.reject(
                                                t('validation.fieldPlaceholdertMinValidation')
                                              );
                                            }
                                            return Promise.resolve();
                                          },
                                        },
                                        {
                                          validator: (_, value) => {
                                            if (!value || value.trim() === '') {
                                              return Promise.resolve();
                                            }

                                            const trimmedLength = value.replace(/\s/g, '').length;
                                            if (trimmedLength > 100) {
                                              return Promise.reject(
                                                t('validation.fieldPlaceholderMaxValidation')
                                              );
                                            }
                                            return Promise.resolve();
                                          },
                                        },
                                      ]}
                                      className="!mb-0"
                                    >
                                      <Input
                                        placeholder={t('placeholder')}
                                        title={t('placeholder')}
                                      />
                                    </Form.Item>
                                  </Col>
                                  <Col xs={24} md={12} lg={6}>
                                    <Form.Item
                                      {...rest}
                                      name={[name, 'dataType']}
                                      rules={[
                                        {
                                          required: true,
                                          message: t('validation.fieldDatatypeRequired'),
                                        },
                                      ]}
                                      className="!mb-0"
                                    >
                                      <Select
                                        placeholder={t('dataType')}
                                        title={t('dataType')}
                                        disabled={isExisting}
                                      >
                                        <Select.Option value="string">
                                          {t('customDataTypes.string')}
                                        </Select.Option>
                                        <Select.Option value="number">
                                          {t('customDataTypes.integer')}
                                        </Select.Option>
                                        <Select.Option value="float">
                                          {t('customDataTypes.float')}
                                        </Select.Option>
                                        <Select.Option value="text">
                                          {t('customDataTypes.text')}
                                        </Select.Option>
                                        <Select.Option value="date">
                                          {t('customDataTypes.date')}
                                        </Select.Option>
                                      </Select>
                                    </Form.Item>
                                  </Col>
                                  <Col xs={18} md={9} lg={5}>
                                    <Form.Item
                                      {...rest}
                                      name={[name, 'required']}
                                      valuePropName="checked"
                                      initialValue={false}
                                      className="!mb-0 text-left md:text-center"
                                    >
                                      <Checkbox>{t('required')}</Checkbox>
                                    </Form.Item>
                                  </Col>
                                  <Col
                                    xs={6}
                                    md={3}
                                    lg={1}
                                    className="flex items-center justify-end text-right"
                                  >
                                    <Trash
                                      onClick={() => remove(name)}
                                      className="mt-3 text-red-500"
                                      size={15}
                                    />
                                  </Col>
                                </Row>
                                {index < fields.length - 1 && (
                                  <Divider className="my-4 bg-gray-300 md:hidden" />
                                )}
                              </React.Fragment>
                            );
                          })}
                          {fields.length === 0 && (
                            <div className="md:text-md mb-4 py-4 text-center text-sm text-gray-500">
                              {t('noCustomFieldsAdded')}
                            </div>
                          )}
                          <Form.Item>
                            <Button
                              type="dashed"
                              icon={<Plus className="mt-1 h-4 w-4" />}
                              onClick={() => add()}
                              className="md:text-md block flex items-center gap-2 text-sm"
                            >
                              {t('addCustomField')}
                            </Button>
                          </Form.Item>{' '}
                        </div>
                      </fieldset>
                    )}
                  </Form.List>
                </>
              )}

              <Form.Item
                name="category"
                label={t('subCategory')}
                validateFirst
                rules={[
                  {
                    validator: (_, value) => {
                      if (value && value.trim() === '') {
                        return Promise.reject(t('subCategoryNameSpaceValidation'));
                      }
                      return Promise.resolve();
                    },
                  },
                  {
                    validator: (_: any, value: any) => {
                      if (!value) return Promise.resolve();
                      // Allowing letters, numbers space comma dash forward slash fullstop and paranthesis
                      // const regex = /^[A-Za-zÁÉÍÓÚáéíóúÑñÜü0-9 ,\-\/\.\(\)]*$/;
                      const regex = REGEX_ALPHA_NUMERIC_MORE_SAFE_FILTER;
                      return regex.test(String(value))
                        ? Promise.resolve()
                        : Promise.reject(t('invalidInput'));
                    },
                  },
                  {
                    validator: (_, value) => {
                      if (!value || value.trim() === '') {
                        return Promise.resolve();
                      }

                      const trimmedLength = value.replace(/\s/g, '').length;
                      if (trimmedLength < 3) {
                        return Promise.reject(t('categoryMinLength'));
                      }
                      return Promise.resolve();
                    },
                  },
                  {
                    validator: (_, value) => {
                      if (!value || value.trim() === '') {
                        return Promise.resolve();
                      }

                      const trimmedLength = value.replace(/\s/g, '').length;
                      if (trimmedLength > 100) {
                        return Promise.reject(t('categoryMaxLength'));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input placeholder={t('categoryPlaceholder')} />
              </Form.Item>
            </Form>
          </div>
          <div className="px-1 md:px-2">
            <Row justify="center" className="my-2">
              <Col span={12} className="pr-2">
                <DefaultButton handleOnClick={handleCancelEdit} width="100%">
                  {t('cancel')}
                </DefaultButton>
              </Col>
              <Col span={12} className="pl-4">
                <PrimaryButton
                  handleOnClick={getUpdateConfirmation}
                  width="100%"
                  hideBgGradient={true}
                >
                  {t('update')}
                </PrimaryButton>
              </Col>
            </Row>
          </div>
        </>
      </Modal>

      <ConfirmModelComponent
        isOpen={isConfirmationOpen}
        onClickedCancel={cancelConfirmation}
        header={isEdit ? t('confirmUpdateTitle') : t('confirmCreateTitle')}
        content={<p>{isEdit ? t('confirmUpdateMessage') : t('confirmCreateMessage')}</p>}
        footerAction={{
          confimation: {
            label: t('confirm'),
            onClick: isEdit ? handleUpdate : handleCreate,
            isDisabled: isConfirmClicked,
          },
          cancel: {
            label: t('cancel'),
            onClick: cancelConfirmation,
          },
        }}
        styleClasses="!w-1/4 min-w-[360px] md:min-w-[380px]"
      />
    </>
  );
};

export default ProductCategoryView;
